// currencyHelpers.js
/**
 * Currency Helper for Web
 * Handles multi-currency pricing, conversion, and formatting
 */

// Supported currency prices structure
export const CurrencyPrices = {
  YER: 'number',
  YRN: 'number',
  USD: 'number',
  SAR: 'number',
  EUR: 'number',
  GBP: 'number',
  AED: 'number',
  EGP: 'number',
  KWD: 'number',
  BHD: 'number',
  QAR: 'number',
  OMR: 'number'
};

// Exchange rates relative to YER (Old Yemeni Rial as base)
export const EXCHANGE_RATES = {
  YER: 1,
  YRN: 0.37,   // 1 YRN = 0.37 YER (1000 YER = 2700 YRN)
  SAR: 140,    // 1 SAR = 140 YER
  USD: 533,    // 1 USD = 533 YER
  EUR: 580,    // 1 EUR = 580 YER
  GBP: 670,    // 1 GBP = 670 YER
  AED: 145,    // 1 AED = 145 YER
  EGP: 17,     // 1 EGP = 17 YER
  KWD: 1735,   // 1 KWD = 1735 YER
  BHD: 1413,   // 1 BHD = 1413 YER
  QAR: 146,    // 1 QAR = 146 YER
  OMR: 1384    // 1 OMR = 1384 YER
};

// Currency symbols mapping
export const CURRENCY_SYMBOLS = {
  YER: 'ر.ي',
  YRN: 'ر.ي.ج',
  USD: '$',
  SAR: 'ر.س',
  EUR: '€',
  GBP: '£',
  AED: 'د.إ',
  EGP: 'ج.م',
  KWD: 'د.ك',
  BHD: 'د.ب',
  QAR: 'ر.ق',
  OMR: 'ر.ع'
};

// Currency names (full)
export const CURRENCY_NAMES = {
  YER: 'ريال يمني قديم',
  YRN: 'ريال يمني جديد',
  USD: 'دولار أمريكي',
  SAR: 'ريال سعودي',
  EUR: 'Euro',
  GBP: 'British Pound',
  AED: 'UAE Dirham',
  EGP: 'Egyptian Pound',
  KWD: 'Kuwaiti Dinar',
  BHD: 'Bahraini Dinar',
  QAR: 'Qatari Riyal',
  OMR: 'Omani Rial'
};

// ✅ Internal function to get current currency synchronously from localStorage
function getCurrentCurrencySync() {
  try {
    const storedCurrency = localStorage.getItem('selectedCurrency');
    return storedCurrency || 'YER';
  } catch {
    return 'YER';
  }
}

/**
 * Get price in selected currency from product's CurrencyPrices object
 * Falls back to conversion if price not available in selected currency
 * ✅ KEPT SYNCHRONOUS - Uses localStorage currency if not provided
 * 
 * @param {Object} item - Product/item object with price data
 * @param {string} [selectedCurrency] - Target currency code (e.g., 'SAR', 'USD')
 * @returns {number} - Price in selected currency
 */
export function getPriceInCurrency(item, selectedCurrency) {
  if (!item) return 0;

  // ✅ If no currency provided, get from localStorage (synchronous)
  const currency = selectedCurrency || getCurrentCurrencySync();

  // 1. Check if item has CurrencyPrices with selected currency
  if (item.CurrencyPrices && typeof item.CurrencyPrices === 'object') {
    const priceInSelectedCurrency = item.CurrencyPrices[currency];
    
    if (priceInSelectedCurrency !== undefined && priceInSelectedCurrency !== null) {
      return parseFloat(priceInSelectedCurrency);
    }

    // 2. Try to convert from another available currency
    const availableCurrencies = Object.keys(item.CurrencyPrices);
    if (availableCurrencies.length > 0) {
      const firstCurrency = availableCurrencies[0];
      const priceInFirstCurrency = parseFloat(item.CurrencyPrices[firstCurrency]);
      
      // Convert: price * (rate of first currency / rate of selected currency)
      if (EXCHANGE_RATES[firstCurrency] && EXCHANGE_RATES[currency]) {
        const converted = priceInFirstCurrency * (EXCHANGE_RATES[firstCurrency] / EXCHANGE_RATES[currency]);
        return converted;
      }
      
      return priceInFirstCurrency;
    }
  }

  // 3. Fallback to Price field with currency conversion
  if (item.Price !== undefined && item.Price !== null) {
    const price = parseFloat(item.Price);
    const itemCurrency = item.Currency || 'YER';
    
    // If item currency matches selected, return as-is
    if (itemCurrency === currency) {
      return price;
    }
    
    // Convert from item currency to selected currency
    if (EXCHANGE_RATES[itemCurrency] && EXCHANGE_RATES[currency]) {
      const converted = price * (EXCHANGE_RATES[itemCurrency] / EXCHANGE_RATES[currency]);
      return converted;
    }
    
    return price;
  }

  return 0;
}

/**
 * Convert delivery fee from YER to target currency
 * ✅ KEPT SYNCHRONOUS
 * 
 * @param {number} feeInYER - Delivery fee in YER
 * @param {string} [targetCurrency] - Target currency code
 * @returns {number} - Converted delivery fee
 */
export function convertDeliveryFee(feeInYER, targetCurrency) {
  // ✅ If no currency provided, get from localStorage (synchronous)
  const currency = targetCurrency || getCurrentCurrencySync();
  
  if (currency === 'YER' || !EXCHANGE_RATES[currency]) {
    return feeInYER;
  }
  return feeInYER / EXCHANGE_RATES[currency];
}

/**
 * Get number of decimal places for currency
 * 
 * @param {string} currency - Currency code
 * @returns {number} - Number of decimal places
 */
export function getCurrencyDecimals(currency) {
  const noDecimalCurrencies = ['YER', 'YRN'];
  const threeDecimalCurrencies = ['KWD', 'BHD', 'OMR'];
  
  if (noDecimalCurrencies.includes(currency)) return 0;
  if (threeDecimalCurrencies.includes(currency)) return 3;
  return 2;
}

/**
 * Format price with proper decimals (no symbol)
 * 
 * @param {number} price - Price value
 * @param {string} currency - Currency code
 * @returns {string} - Formatted price string
 */
export function formatCurrencyPrice(price, currency) {
  const decimals = getCurrencyDecimals(currency);
  return price.toFixed(decimals);
}

/**
 * Format price with currency symbol
 * ✅ KEPT SYNCHRONOUS
 * 
 * @param {number} amount - Price amount
 * @param {string} [currency] - Currency code
 * @param {number} [decimals] - Optional: override decimal places
 * @returns {string} - Formatted price with symbol
 */
export function formatPrice(amount, currency, decimals) {
  // ✅ If no currency provided, get from localStorage (synchronous)
  const finalCurrency = currency || getCurrentCurrencySync();
  
  const symbol = CURRENCY_SYMBOLS[finalCurrency] || finalCurrency;
  const decimalPlaces = decimals !== undefined ? decimals : getCurrencyDecimals(finalCurrency);
  return `${amount.toFixed(decimalPlaces)} ${symbol}`;
}

/**
 * Get currency symbol
 * 
 * @param {string} currency - Currency code
 * @returns {string} - Currency symbol
 */
export function getCurrencySymbol(currency) {
  return CURRENCY_SYMBOLS[currency] || currency;
}

/**
 * Get currency name
 * 
 * @param {string} currency - Currency code
 * @returns {string} - Full currency name
 */
export function getCurrencyName(currency) {
  return CURRENCY_NAMES[currency] || currency;
}

/**
 * Convert amount from one currency to another
 * 
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency
 * @param {string} toCurrency - Target currency
 * @returns {number} - Converted amount
 */
export function convertCurrency(amount, fromCurrency, toCurrency) {
  if (fromCurrency === toCurrency) return amount;
  
  if (!EXCHANGE_RATES[fromCurrency] || !EXCHANGE_RATES[toCurrency]) {
    console.warn(`Currency conversion not available: ${fromCurrency} → ${toCurrency}`);
    return amount;
  }
  
  return amount * (EXCHANGE_RATES[fromCurrency] / EXCHANGE_RATES[toCurrency]);
}

/**
 * Get all supported currencies
 * 
 * @returns {Array} - Array of currency objects
 */
export function getSupportedCurrenciesList() {
  return Object.keys(EXCHANGE_RATES).map(code => ({
    code,
    name: CURRENCY_NAMES[code] || code,
    symbol: CURRENCY_SYMBOLS[code] || code,
    rate: EXCHANGE_RATES[code]
  }));
}

/**
 * Format price for display in UI
 * 
 * @param {Object} item - Item with price data
 * @param {string} currency - Selected currency
 * @param {boolean} [withSymbol=true] - Include currency symbol
 * @returns {string} - Formatted price string
 */
export function displayPrice(item, currency, withSymbol = true) {
  const price = getPriceInCurrency(item, currency);
  
  if (withSymbol) {
    return formatPrice(price, currency);
  }
  
  return formatCurrencyPrice(price, currency);
}

/**
 * Calculate discount percentage
 * 
 * @param {number} originalPrice - Original price
 * @param {number} discountedPrice - Discounted price
 * @returns {number} - Discount percentage (0-100)
 */
export function calculateDiscountPercentage(originalPrice, discountedPrice) {
  if (!originalPrice || originalPrice <= discountedPrice) return 0;
  return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
}

/**
 * Parse price from various formats
 * 
 * @param {*} value - Price value (string, number, etc.)
 * @returns {number} - Parsed price
 */
export function parsePrice(value) {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.]/g, '');
    return parseFloat(cleaned) || 0;
  }
  return 0;
}

// ✅ Helper function to clear currency on logout (call this in your logout function)
export function clearCurrencyOnLogout() {
  try {
    localStorage.removeItem('userId');
    localStorage.removeItem('selectedCurrency');
    console.log('✅ Currency cleared on logout');
  } catch (error) {
    console.error('❌ Error clearing currency:', error);
  }
}

// ✅ Helper function to force sync currency (call after login/register)
export async function syncCurrencyAfterAuth(userId) {
  try {
    localStorage.setItem('userId', userId.toString());
    
    // Fetch currency from backend
    const { getUserCurrency } = await import('./currencyApi');
    const backendCurrency = await getUserCurrency(userId);
    
    // Check for mismatch
    const localCurrency = localStorage.getItem('selectedCurrency');
    if (localCurrency && localCurrency !== backendCurrency) {
      console.warn(
        `🔄 Currency mismatch detected!\n` +
        `Local: ${localCurrency} → Backend: ${backendCurrency}\n` +
        `Auto-correcting to backend value.`
      );
    }
    
    // Update localStorage with backend value
    localStorage.setItem('selectedCurrency', backendCurrency);
    
    console.log('✅ Currency synced after auth:', backendCurrency);
    
    // Dispatch event for components to update
    window.dispatchEvent(new CustomEvent('currencyChanged', { detail: { currency: backendCurrency } }));
    
    return backendCurrency;
  } catch (error) {
    console.error('❌ Error syncing currency after auth:', error);
    return 'YER';
  }
}

// ✅ Helper to get current currency synchronously (for display purposes)
export function getCurrentCurrency() {
  return getCurrentCurrencySync();
}

// Default export for convenience
export default {
  EXCHANGE_RATES,
  CURRENCY_SYMBOLS,
  CURRENCY_NAMES,
  getPriceInCurrency,
  convertDeliveryFee,
  getCurrencyDecimals,
  formatCurrencyPrice,
  formatPrice,
  getCurrencySymbol,
  getCurrencyName,
  convertCurrency,
  getSupportedCurrenciesList,
  displayPrice,
  calculateDiscountPercentage,
  parsePrice,
  clearCurrencyOnLogout,
  syncCurrencyAfterAuth,
  getCurrentCurrency
};